// sellsy.ts — generator of the Sellsy interface contract.
//
// Produces, per quote line, a clean data structure plus the text description block for the Sellsy
// API layer. Strictly text and data generation: no POST, no network call, no UI markup. The API
// layer carries this contract without recomputing or reformatting.
import { CATALOGUE, PRINTS, CUTS, LAMINATIONS, FINISHINGS, LOGISTICS_OPTS } from "../../data";
import { getItemBound } from "../catalogue/lookup";
import { type QuoteLine, num, round2 } from "../core/core";
import { engineMessage, messageParam } from "../core/messages";
import {
  isAccessoryMlRegime,
  isLargeFormatPrintRegime,
  isServiceRegime,
  isUnitRegime,
} from "../core/regimes";
import { isCustomSizeSurcharge } from "../products/custom-size-line";
import { extraTitle } from "../products/custom-size-presentation";
import { displayNumMap } from "../products/triggers";
import type { ServiceLineCalc, ServiceQuoteLine } from "../products/services-pricing";
import type { QuoteState, PricedLine, QuoteResult } from "../compute/compute";
import { backPlanningDefaults, fileReceptionLeadDays } from "./back-planning";
import { quoteDeliveryLines } from "./quote-delivery";

/** Max length of the merged document note accepted by `publish-quote-calculation` (Sellsy estimate `note`). */
export const ESTIMATE_DOCUMENT_NOTES_MAX_LENGTH = 8000;

/**
 * Length of a plain-text footer after the same HTML escaping + `<br>` encoding as
 * `formatEstimateDocumentNote` on the edge (so `&` / `<` cannot sneak past the 8000 cap).
 * @param plain Merged plain-text notes
 * @returns Encoded length
 */
export function estimateDocumentNoteEncodedLength(plain: string): number {
  const normalized = plain
    .split("\n")
    .map((l) => l.trimEnd())
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
  if (!normalized) return 0;
  const escaped = normalized
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
  return escaped.replace(/\n/g, "<br>").length;
}

/**
 * Max length of `state.header.pdfComment` — the rest of the footer (labels, delivery notes, production block)
 * consumes part of {@link ESTIMATE_DOCUMENT_NOTES_MAX_LENGTH}.
 */
export const PDF_COMMENT_MAX_LENGTH = 3500;

/** Extra context for footer notes of the estimate (not line pricing). */
export interface EstimateNotesContext {
  /** Nexus quote number (`DEV-…`, `-A…`, `-R…`) for the edition header. */
  quoteNumber?: string | null;
  /** Last persistence time of the calculation row (edition timestamp when after validation). */
  updatedAt?: string | null;
  /** When the quote was last validated / frozen. */
  validatedAt?: string | null;
  /** Contractual delivery date (`quote_calculations.delivery_date`), ISO day. */
  deliveryDate?: string | null;
  /** Free comment printed under the product lines only when non-empty. */
  pdfComment?: string | null;
}

/** One line of the Sellsy contract: structured data plus the rendered description block. */
export interface SellsyLine {
  /** Line designation, uppercased (Sellsy convention). */
  title: string;
  /** Total quantity = number of files × copies per file. */
  quantity: number;
  /** Net unit price (per piece), in euros, excluding VAT. */
  unitPriceExclVat: number;
  /** Net line total, in euros, excluding VAT. */
  totalExclVat: number;
  /** Description block: copy-pastable multi-line text. */
  descriptionBlock: string;
}

/**
 * Material display name, falling back to the reference.
 * @param ref The material reference.
 * @returns The catalogue name, or the reference when unknown.
 */
const materialName = (ref: string) => CATALOGUE.find((m) => m.ref === ref)?.name ?? ref;

/**
 * Short material label for line titles (catalogue `itemShort`, not the ref code).
 * @param ref The material reference.
 * @returns The short client-facing name.
 */
function materialShortLabel(ref: string): string {
  const short = getItemBound(ref)?.itemShort?.trim();
  return short || materialName(ref);
}

/**
 * Print mode display label, falling back to the code.
 * @param code The print mode code.
 * @returns The label, or the code when unknown.
 */
const printLabel = (code: string) => PRINTS.find((p) => p.code === code)?.label ?? code;

/**
 * Cut display label, falling back to the code.
 * @param code The cut code.
 * @returns The label, or the code when unknown.
 */
const cutLabel = (code: string) => CUTS.find((c) => c.code === code)?.label ?? code;

/**
 * Whether a label says « nothing selected » (empty, « aucune », « none », a dash): such a value is never printed.
 * @param label The label.
 * @returns True when there is nothing to show.
 */
const isNoValue = (label: string | null | undefined): boolean => !label || /^\s*(?:-|—|aucune?|none)\s*$/i.test(label);

/**
 * Cut as the client reads it: « Coupe droite rigide » becomes « Coupe droite - matière rigide ».
 * @param label The cut label.
 * @returns The label, with the material nature spelled out when it ends with it.
 */
function cutDescription(label: string): string {
  return label.replace(/^(.*\S)\s+(souple|rigide)$/i, "$1 - matière $2");
}

/**
 * Lamination and finishings of a print line as the client reads them (long label when the catalogue has one),
 * « aucune » / unknown-empty values left out.
 * @param l The print quote line.
 * @returns The lamination label (or null) and the finishing labels, in the order of the line.
 */
export function printComponentLabels(l: QuoteLine): { lamination: string | null; finishings: string[] } {
  const lam = l.lamination ? (LAMINATIONS.find((x) => x.code === l.lamination)?.label ?? l.lamination) : null;
  const codes = [l.finishing1, l.finishing2, l.finishing3, l.finishing4, l.finishing5, l.finishing6];
  const finishings = codes
    .map((code) => {
      if (!code?.trim()) return null;
      const f = FINISHINGS.find((x) => x.code === code);
      return f?.fullLabel?.trim() || f?.label?.trim() || code;
    })
    .filter((label): label is string => !isNoValue(label));
  return { lamination: isNoValue(lam) ? null : lam, finishings };
}

/**
 * Designation of a catalogue material as the client reads it: its full name, parts separated by « / ».
 * @param ref The material reference.
 * @returns The full name (short name when the catalogue has none).
 */
function materialDesignation(ref: string): string {
  const full = getItemBound(ref)?.fullName?.trim();
  return full ? full.replace(/\s-\s/g, " / ") : materialName(ref);
}

/**
 * One labelled description line when the value is present (empty / « aucune » → omitted).
 * @param key Engine message key of the `Label : {{value}}` form
 * @param value Display value
 * @returns Encoded message, or null when there is nothing to print
 */
function labelledLine(key: string, value: string | null | undefined): string | null {
  if (isNoValue(value)) return null;
  return engineMessage(key, { value: value!.trim() });
}

/**
 * Confection rows of a quote cell, one per confection: numbered when there are several, so the client reads them apart.
 * @param finishings Labels of the confections, in slot order
 * @returns Encoded rows (none without confection)
 */
export function finishingLines(finishings: readonly string[]): string[] {
  if (finishings.length === 1) return [engineMessage("engineSellsyFinishingLabel", { value: finishings[0]! })];
  return finishings.map((value, i) => engineMessage("engineSellsyFinishingNumLabel", { n: String(i + 1), value }));
}

/**
 * Title of a print line: `{n} - {file name} - {short material}` (material omitted when unknown).
 * Missing file name → nested `engineFileFallback` (`Fichier {n}`), resolved at publish.
 * Whole title is one engine message so nested keys resolve (never leak `engineFileFallback|…`).
 * @param l The print quote line
 * @param displayNum Persistent display number of the line
 * @returns Encoded title
 */
function printTitle(l: QuoteLine, displayNum: string): string {
  const entered = l.fileName?.trim();
  const file = entered
    ? entered
    : messageParam(engineMessage("engineFileFallback", { num: displayNum }));
  const mat = l.material?.trim() ? materialShortLabel(l.material.trim()) : "";
  return mat
    ? engineMessage("engineSellsyPrintTitle", { num: displayNum, file, mat })
    : engineMessage("engineSellsyPrintTitleNoMat", { num: displayNum, file });
}

/**
 * Description block of a print line — one labelled row per filled field, never an empty label:
 * Impression, Support (full material), Coupe, Plastif, Confection, Format, Fichiers (count + copies), Quantité (m² when known).
 * Optional flat-fee mention last.
 * @param l The print quote line.
 * @param row Its priced line.
 * @param opts Extra tail lines (flat-fee mention, …)
 * @returns The block (engine message lines, `\n` separated).
 */
function printDescription(
  l: QuoteLine,
  row: PricedLine,
  opts: { flatFeeApplied?: boolean } = {},
): string {
  const cutRaw = l.cut ? cutLabel(l.cut) : null;
  const cut = cutRaw && !isNoValue(cutRaw) ? cutDescription(cutRaw) : null;
  const components = printComponentLabels(l);
  const files = num(l.fileQty) || 1;
  const copies = num(l.copiesPerFile) || 1;
  const lines = [
    labelledLine("engineSellsyPrintLabel", l.print ? printLabel(l.print) : null),
    labelledLine("engineSellsySupportLabel", l.material ? materialDesignation(l.material) : null),
    labelledLine("engineSellsyCutLabel", cut),
    labelledLine("engineSellsyLaminationLabel", components.lamination),
    ...finishingLines(components.finishings),
    l.L && l.H
      ? engineMessage("engineSellsyFormatMm", { width: String(num(l.L)), height: String(num(l.H)) })
      : null,
    engineMessage("engineSellsyFilesDetail", { files, copies }),
    // Material area needed for the print (omit when unknown / zero).
    row.m2 > 0 ? engineMessage("engineSellsyQtyArea", { area: String(round2(row.m2)) }) : null,
    opts.flatFeeApplied ? engineMessage("engineSellsyFlatFeeNote") : null,
    // The « raccord » box (ticked by default when the piece does not fit) states the joins at the very end of the cell:
    // a seam between the widths for a textile (code TEX…), joined pieces otherwise.
    row.spliced
      ? engineMessage(/^TEX/i.test(l.material?.trim() ?? "") ? "engineSellsyTexSeam" : "engineSpliceNotice")
      : null,
  ];
  return lines.filter((x): x is string => !!x).join("\n");
}

/**
 * Option C description: client-facing notice only. Qty / unit / total live in Sellsy columns —
 * no price cascade in the description cell.
 * @param notice Engine or free client notice (e.g. splice), if any.
 * @returns Trimmed notice, or empty when none.
 */
export function sellsyDescriptionBlock(notice?: string | null): string {
  return notice?.trim() ?? "";
}

/**
 * Client line of a custom-size surcharge child (STAND type, no matRef — still billed to the client).
 * Title = first wording line; description = the rest (no price totals — Sellsy columns carry them).
 * @param l The surcharge quote line.
 * @param row Its priced line.
 * @returns The Sellsy line.
 */
function customSizeLine(l: QuoteLine, row: PricedLine): SellsyLine {
  const wording = extraTitle(l.customSizeFinal, row.customSize);
  return {
    title: wording?.[0] ?? engineMessage("engineSellsyCustomSizeFallback"),
    quantity: row.quantity,
    unitPriceExclVat: row.pu ?? 0,
    totalExclVat: row.total,
    descriptionBlock: wording && wording.length > 1 ? wording.slice(1).join("\n") : "",
  };
}

/**
 * Client line of a catalogue large-format print line (PDF modèle: numbered file title + labelled block).
 * @param l The print quote line.
 * @param row Its priced line.
 * @param displayNum Persistent display number of the line
 * @param pricing Optional amount override (minimum flat fee absorbed onto this print line)
 * @returns The Sellsy line.
 */
function printLine(
  l: QuoteLine,
  row: PricedLine,
  displayNum: string,
  pricing?: { totalExclVat: number; flatFeeApplied?: boolean },
): SellsyLine {
  const files = num(l.fileQty) || 1;
  const copies = num(l.copiesPerFile) || 1;
  const quantity = row.quantity > 0 ? row.quantity : files * copies || 1;
  const totalExclVat = pricing?.totalExclVat ?? row.total;
  return {
    title: printTitle(l, displayNum),
    quantity,
    unitPriceExclVat: unitFromTotal(totalExclVat, quantity),
    totalExclVat,
    descriptionBlock: printDescription(l, row, { flatFeeApplied: pricing?.flatFeeApplied }),
  };
}

/**
 * Sellsy contract of the quote: one SellsyLine per priced PRINT_GF line (and custom-size surcharge
 * child), excluding lines covered by a minimum flat fee. Other services/fees stay out of scope of
 * THIS function (the contract of the print lines); the whole client document is {@link buildEstimateLines}.
 * @param res The quote result.
 * @param state The quote state the result was computed from.
 * @returns The Sellsy lines.
 */
export function buildSellsyLines(res: QuoteResult, state: QuoteState): SellsyLine[] {
  const out: SellsyLine[] = [];
  const displayNums = displayNumMap(state.lines);
  res.lines.forEach((row, i) => {
    const l = state.lines[i];
    if (!l || row.flatFeeReplaced || !(row.total > 0)) return;
    if (isCustomSizeSurcharge(l)) {
      out.push(customSizeLine(l, row));
      return;
    }
    if (!isLargeFormatPrintRegime(l) || row.matRef == null) return;
    const displayNum = (l.id != null ? displayNums.get(l.id) : null) ?? String(l.num ?? i + 1);
    out.push(printLine(l, row, displayNum));
  });
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────────────────────
// Whole client document: every priced element of the quote, not only the print lines.
//
// PRESENTATION CHOICES (the sum of the lines is ALWAYS `result.totalFinal`, to the cent):
//  - Option C descriptions: designation in `title`, optional client notice in `descriptionBlock`.
//    Qty / unit / total stay in Sellsy columns — no €/m² cascade, no "Quantité / Total" echo in the cell.
//  - Every other priced line (stands, transfers, accessories, joinery, custom units / prints, free lines,
//    generated services) is one client line with its designation, quantity, unit price and total.
//  - Minimum flat fees (`flatFeeLines`) are one client line each; the lines they replace weigh 0 and
//    never reach the client (same rule as the contract).
//  - Services: one line each; the two delivery blocks follow `quoteDeliveryLines` (detail, or ONE flat-fee
//    line when the block is on flat fee) but are priced from the engine's own service lines, so a client
//    rate-card price can never drift from the total.
//  - The GENERAL DISCOUNT is ONE explicit negative line at the foot of the quote, and the RUSH ONE positive line
//    after it. This is the document lock of book 4.10 §5 (RG-8, proved by `sellsy-discount-target.spec.ts`):
//    a general discount changes NOTHING line by line; the foot-of-quote negotiation is stated once, globally.
//    Spreading the discount over the unit prices would make it invisible and unrecoverable; the rush is
//    applied AFTER the discount and is not subject to it, so it can only be a line of its own.
//  - Ancillary purchase fees, purchase prices, margins and production notes are never client lines.
// ─────────────────────────────────────────────────────────────────────────────────────────────

/** Nature of a client line of the estimate. */
export type EstimateLineKind =
  | "print"
  | "custom-size"
  | "item"
  | "flat-fee"
  | "service"
  | "delivery"
  | "general-discount"
  | "rush";

/** One client line of the estimate: the contract line plus the Sellsy product reference and its nature. */
export interface EstimateLine extends SellsyLine {
  /** Product reference printed in the Sellsy `reference` column (catalogue ref, or a stable label). */
  reference: string;
  /** Nature of the line. */
  kind: EstimateLineKind;
}

/** Reference printed for lines that carry no catalogue ref, by line type. */
const FALLBACK_REFERENCE: Partial<Record<QuoteLine["type"], string>> = {
  SUR_PRINT: "SUR-PRINT",
  SUR_UNIT: "SUR-UNIT",
  MENUIS_FAB: "MENUIS-FAB",
  PRINT_GF: "LIBRE",
};

/** Service keys that belong to a delivery block, with the flat-fee message key of the block. */
const DELIVERY_BLOCKS = {
  logistics: { flatKey: "engineLogisticsFlatFee" },
  endSite: { flatKey: "engineEndSiteFlatFee" },
} as const;

/** One of the two delivery block keys of `serviceItem.lines`. */
type DeliveryKey = keyof typeof DELIVERY_BLOCKS;

/**
 * Whether a service line key is one of the two delivery blocks.
 * @param key The service line key.
 * @returns True for `logistics` and `endSite`.
 */
const isDeliveryKey = (key: string): key is DeliveryKey => key in DELIVERY_BLOCKS;

/**
 * Sum of the totals of the lines, to the cent (each total is rounded to the cent first, then summed as integers).
 * @param lines The estimate lines.
 * @returns The total excl. VAT in euros.
 */
export function estimateLinesTotal(lines: readonly Pick<SellsyLine, "totalExclVat">[]): number {
  return lines.reduce((cents, l) => cents + Math.round(l.totalExclVat * 100), 0) / 100;
}

/**
 * Unit price shown for a total split over a quantity.
 * @param total The line total.
 * @param quantity The billed quantity.
 * @returns The total over the quantity, to the cent (the total itself for a null quantity).
 */
const unitFromTotal = (total: number, quantity: number): number => (quantity > 0 ? round2(total / quantity) : total);

/**
 * Client designation of a non-print line: the per-line full name, else the catalogue full name, else the free
 * label, else the material name.
 * @param l The quote line.
 * @param row Its priced line.
 * @returns The designation (never empty).
 */
function designation(l: QuoteLine, row: PricedLine): string {
  const fullName = l.fullName?.trim();
  if (fullName) return fullName;
  const catalogueRef = isServiceRegime(l) ? l.svcRef : l.ref;
  const item = catalogueRef ? getItemBound(catalogueRef) : undefined;
  if (item) return item.fullName || item.itemShort;
  const free = l.offCatalogue?.trim();
  if (free) return free;
  if (l.material) return materialName(l.material);
  return row.name?.trim() || engineMessage("engineSellsyPrestation");
}

/**
 * Quantity wording of a generic line: files × copies for the print-like and tier lines, linear metres, hours or units otherwise.
 * @param l The quote line.
 * @param row Its priced line.
 * @returns The quantity part of the title (encoded message or structural unit code).
 */
function quantityWording(l: QuoteLine, row: PricedLine): string {
  if (isAccessoryMlRegime(l)) return engineMessage("engineSellsyQtyMl", { qtyDec2: row.quantity });
  if (isServiceRegime(l)) return engineMessage("engineSellsyQtyHours", { qtyDec2: row.quantity });
  if (isUnitRegime(l)) return engineMessage("engineSellsyQtyUnits", { qty: row.quantity });
  return engineMessage("engineSellsyQtyFiles", {
    files: num(l.fileQty) || 1,
    copies: num(l.copiesPerFile) || 1,
    qty: row.quantity,
  });
}

/**
 * Files and quantities printed under the designation of a product line, like a print line (stand, transfer, joinery,
 * custom unit...). Services and metre accessories have no file: nothing is printed for them.
 * @param l The quote line.
 * @param row Its priced line.
 * @returns The lines to print (empty when the line carries no file).
 */
function itemFigures(l: QuoteLine, row: PricedLine): string[] {
  if (isServiceRegime(l) || isAccessoryMlRegime(l)) return [];
  return [
    engineMessage("engineSellsyFilesCopies", { files: num(l.fileQty) || 1, copies: num(l.copiesPerFile) || 1 }),
    engineMessage("engineSellsyTotalQuantity", { qty: row.quantity }),
  ];
}

/**
 * Client line of any priced line that is not a catalogue print: stand, transfer, accessory, joinery, custom unit
 * or print, free line, generated service.
 * @param l The quote line.
 * @param row Its priced line.
 * @returns The estimate line.
 */
function itemLine(l: QuoteLine, row: PricedLine): EstimateLine {
  const hasDims = !!(l.L && l.H);
  const dimsLabel = hasDims
    ? engineMessage("engineSellsyFormat", { width: String(num(l.L)), height: String(num(l.H)) })
    : null;
  // The title is the designation alone (the quantity has its own column); the size, when there is one, goes below.
  const unit = row.pu ?? unitFromTotal(row.total, row.quantity);
  return {
    title: designation(l, row),
    quantity: row.quantity,
    unitPriceExclVat: unit,
    totalExclVat: row.total,
    descriptionBlock: [dimsLabel, ...itemFigures(l, row), sellsyDescriptionBlock(row.clientNotice)].filter(Boolean).join("\n"),
    reference: l.ref || l.svcRef || l.material || FALLBACK_REFERENCE[l.type] || "LIBRE",
    kind: "item",
  };
}

/**
 * Client lines of the priced quote lines (print contract lines first-class, everything else generic).
 * Print lines covered by a minimum flat fee still appear with their full PDF détail: the fee amount is
 * absorbed on the first replaced print of that material (never a bare « Adh. … / Forfait » line that
 * hides Impression / Support / Coupe / …).
 * @param res The quote result.
 * @param state The quote state the result was computed from.
 * @returns Quote lines plus the material refs whose flat fee was absorbed onto a print line.
 */
function quoteLineEntries(
  res: QuoteResult,
  state: QuoteState,
): { lines: EstimateLine[]; absorbedFlatFeeRefs: Set<string> } {
  const out: EstimateLine[] = [];
  const absorbedFlatFeeRefs = new Set<string>();
  const displayNums = displayNumMap(state.lines);
  /** Flat-fee amount still to place on a print line, keyed by material ref. */
  const feeLeft = new Map(res.flatFeeLines.filter((f) => f.amount !== 0).map((f) => [f.ref, f.amount] as const));
  res.lines.forEach((row, i) => {
    const l = state.lines[i];
    if (!l) return;
    const isPrint = isLargeFormatPrintRegime(l) && row.matRef != null && !row.offCatalogue;
    // Flat-fee-replaced lines keep their détail on the PDF: the fee lands on the first replaced line of the
    // material (keyed like the fee, by catalogue key), and carries the mention at the foot of its cell.
    if (row.flatFeeReplaced) {
      const feeKey = row._catKey ?? row.matRef ?? "";
      const feeAmount = feeLeft.get(feeKey) ?? 0;
      if (!isPrint) {
        if (!feeAmount || isCustomSizeSurcharge(l)) return;
        feeLeft.set(feeKey, 0);
        absorbedFlatFeeRefs.add(feeKey);
        const item = itemLine(l, { ...row, total: feeAmount, pu: unitFromTotal(feeAmount, row.quantity) });
        out.push({
          ...item,
          descriptionBlock: [item.descriptionBlock, engineMessage("engineSellsyFlatFeeNote")].filter(Boolean).join("\n"),
        });
        return;
      }
      const displayNum = (l.id != null ? displayNums.get(l.id) : null) ?? String(l.num ?? i + 1);
      if (feeAmount) {
        feeLeft.set(feeKey, 0);
        absorbedFlatFeeRefs.add(feeKey);
      }
      const line = printLine(l, row, displayNum, {
        totalExclVat: feeAmount,
        flatFeeApplied: feeAmount > 0,
      });
      out.push({ ...line, reference: row.matRef!, kind: "print" });
      return;
    }
    if (row.total === 0) return;
    if (isCustomSizeSurcharge(l)) {
      out.push({ ...customSizeLine(l, row), reference: row.customSize?.rateRef ?? "SUR-MESURE", kind: "custom-size" });
    } else if (isPrint) {
      const displayNum = (l.id != null ? displayNums.get(l.id) : null) ?? String(l.num ?? i + 1);
      const line = printLine(l, row, displayNum);
      out.push({
        ...line,
        reference: row.matRef!,
        kind: "print",
      });
    } else {
      out.push(itemLine(l, row));
    }
  });
  return { lines: out, absorbedFlatFeeRefs };
}

/**
 * Residual minimum flat fees that could not be absorbed onto a print line (no replaced print for that ref).
 * @param res The quote result.
 * @param absorbedRefs Material refs whose fee was already placed on a print line
 * @returns One estimate line per remaining flat fee.
 */
function flatFeeEntries(res: QuoteResult, absorbedRefs: ReadonlySet<string>): EstimateLine[] {
  return res.flatFeeLines
    .filter((f) => f.amount !== 0 && !absorbedRefs.has(f.ref))
    .map((f) => ({
      title: engineMessage("engineSellsyFlatFeeTitle", { name: f.name }),
      quantity: 1,
      unitPriceExclVat: f.amount,
      totalExclVat: f.amount,
      descriptionBlock: engineMessage("engineSellsyFlatFeeApplied"),
      reference: f.ref,
      kind: "flat-fee" as const,
    }));
}

/**
 * Client lines of one delivery block: the detailed lines, or ONE flat-fee line (sum of the engine totals).
 * @param key The delivery block key.
 * @param calcs The engine's service lines of the block.
 * @param entered The block's lines as entered (same filter as the engine, so index-aligned with `calcs`).
 * @param flatFee Whether the block is on flat fee.
 * @returns The estimate lines of the block.
 */
function deliveryEntries(
  key: DeliveryKey,
  calcs: readonly ServiceLineCalc[],
  entered: readonly ServiceQuoteLine[],
  flatFee: boolean,
): EstimateLine[] {
  const reference = `SVC-${key.toUpperCase()}`;
  if (flatFee) {
    const sum = round2(calcs.reduce((s, c) => s + c.total, 0));
    return [{
      title: engineMessage(DELIVERY_BLOCKS[key].flatKey),
      quantity: 1,
      unitPriceExclVat: sum,
      totalExclVat: sum,
      descriptionBlock: "",
      reference,
      kind: "delivery",
    }];
  }
  return calcs.map((c, i) => {
    const note = (entered[i]?.note ?? "").trim();
    return {
      title: note
        ? engineMessage("engineSellsyServiceTitleWithNote", { label: messageParam(c.label), note })
        : c.label,
      quantity: c.quantity,
      unitPriceExclVat: unitFromTotal(c.total, c.quantity),
      totalExclVat: c.total,
      descriptionBlock: "",
      reference,
      kind: "delivery" as const,
    };
  });
}

/**
 * Bare title of a studio service whose label carries a calculation, for a result frozen before the bare title was
 * stored on the line (`clientLabel`): the mode of the service tells which title applies.
 * @param key The service line key.
 * @param state The quote state.
 * @returns The title (engine message), or null when the service has no calculation in its label.
 */
function bareServiceTitle(key: string, state: QuoteState): string | null {
  const services = state.services;
  if (key === "info") return engineMessage(services?.infoMode === "hourly" ? "engineSvcInfoHourly" : "engineSvcInfoFile");
  if (key === "packaging") {
    return engineMessage(services?.packagingMode === "hourly" ? "engineSvcPackagingHourly" : "engineSvcPackagingElement");
  }
  if (key === "gp") return engineMessage("engineSvcProjectManagement");
  if (key === "calibration") return engineMessage("engineSvcColourCalibrationShort");
  return null;
}

/**
 * Client lines of the services (delivery blocks per `quoteDeliveryLines`, other services one line each, zero-priced skipped).
 * @param res The quote result.
 * @param state The quote state.
 * @returns The estimate lines of the services, in the engine's order.
 */
function serviceEntries(res: QuoteResult, state: QuoteState): EstimateLine[] {
  const services = state.services ?? {};
  const delivery = quoteDeliveryLines(services);
  /**
   * Entered lines the engine kept for a delivery block: code set, quantity > 0, known option.
   * @param arr The block's entered lines.
   * @returns The lines the engine priced, in order.
   */
  const keptLines = (arr: readonly ServiceQuoteLine[] | undefined) =>
    (arr ?? []).filter((x) => x.code && (num(x.quantity) || 0) > 0 && LOGISTICS_OPTS.some((o) => o.code === x.code));
  const blocks: Record<DeliveryKey, { entered: ServiceQuoteLine[]; flatFee: boolean }> = {
    logistics: { entered: keptLines(services.logistics), flatFee: delivery.outbound.flatFee },
    endSite: { entered: keptLines(services.endSite), flatFee: delivery.endSite.flatFee },
  };
  const emitted = new Set<DeliveryKey>();
  const out: EstimateLine[] = [];
  for (const c of res.serviceItem.lines) {
    if (isDeliveryKey(c.key)) {
      if (emitted.has(c.key)) continue;
      emitted.add(c.key);
      const calcs = res.serviceItem.lines.filter((x) => x.key === c.key);
      out.push(...deliveryEntries(c.key, calcs, blocks[c.key].entered, blocks[c.key].flatFee));
    } else if (c.total !== 0) {
      out.push({
        title: c.clientLabel ?? bareServiceTitle(c.key, state) ?? c.label,
        quantity: c.quantity,
        unitPriceExclVat: unitFromTotal(c.total, c.quantity),
        totalExclVat: c.total,
        descriptionBlock: "",
        reference: `SVC-${c.key.toUpperCase()}`,
        kind: "service",
      });
    }
  }
  return out;
}

/**
 * Foot-of-quote lines: the general discount (negative) then the rush (positive, applied after the discount).
 * @param res The quote result.
 * @returns The estimate lines of the foot, possibly empty.
 */
function footEntries(res: QuoteResult): EstimateLine[] {
  const out: EstimateLine[] = [];
  const discount = res.generalDiscountAmountVal;
  if (discount > 0) {
    out.push({
      title: engineMessage("engineSellsyGeneralDiscountTitle"),
      quantity: 1,
      unitPriceExclVat: -discount,
      totalExclVat: -discount,
      descriptionBlock: engineMessage("engineSellsyGeneralDiscountDesc"),
      reference: "REMISE",
      kind: "general-discount",
    });
  }
  if (res.rush.amount > 0) {
    out.push({
      title: res.rush.label ? res.rush.label : engineMessage("engineSellsyRushTitle"),
      quantity: 1,
      unitPriceExclVat: res.rush.amount,
      totalExclVat: res.rush.amount,
      descriptionBlock: engineMessage("engineSellsyRushDesc"),
      reference: "RUSH",
      kind: "rush",
    });
  }
  return out;
}

/**
 * The WHOLE client document of the quote: one line per priced element, whose sum (to the cent) is
 * `res.totalFinal`. Order: quote lines, minimum flat fees, services, general discount, rush.
 * Pure text and data generation, like the rest of the contract: no network call.
 * @param res The quote result (computed or frozen).
 * @param state The quote state the result was computed from.
 * @returns The estimate lines.
 */
export function buildEstimateLines(res: QuoteResult, state: QuoteState): EstimateLine[] {
  const { lines: quoteLines, absorbedFlatFeeRefs } = quoteLineEntries(res, state);
  return [
    ...quoteLines,
    ...flatFeeEntries(res, absorbedFlatFeeRefs),
    ...serviceEntries(res, state),
    ...footEntries(res),
  ];
}

/**
 * Formats an ISO day for the PDF (`dd/mm/yyyy`).
 * @param iso ISO day
 * @returns French date, or empty when unusable
 */
function formatIsoDayFr(iso: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? "");
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
}

/**
 * Formats an ISO timestamp for the PDF edition line (`dd/mm/yyyy à HH:mm`, Europe/Paris).
 * Date and time both come from Europe/Paris so they never disagree around midnight UTC.
 * @param iso ISO date-time
 * @returns French date-time, or empty when unusable
 */
function formatIsoDateTimeFr(iso: string | null | undefined): string {
  if (!iso?.trim()) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const parts = new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "Europe/Paris",
  }).formatToParts(d);
  const get = (type: Intl.DateTimeFormatPartTypes): string => parts.find((p) => p.type === type)?.value ?? "";
  const day = get("day");
  const month = get("month");
  const year = get("year");
  const hour = get("hour");
  const minute = get("minute");
  if (!day || !month || !year || !hour || !minute) return "";
  return `${day}/${month}/${year} à ${hour}:${minute}`;
}

/**
 * Free-text footer for the Sellsy estimate document `note` field (outside the lines table).
 * Order: devis / édition, date de livraison, optional PDF comment (prefixed « Commentaire : »), delivery notes,
 * CONDITIONS DE PRODUCTION.
 * Sellsy has no free-form PDF *header* API field beyond the estimate subject; the edition line therefore
 * lives in `note` (footer / document notes), which is the supported surface for this metadata.
 * Payment method / delay are NOT duplicated here — Sellsy already prints « Moyen de règlement »
 * beside the totals via `payment_method_ids` / `payment_terms`; repeating them in the comment
 * pushes that native block onto the next page.
 * Empty PDF comment → omitted.
 * @param res The quote result.
 * @param state The quote state.
 * @param ctx Quote number, edition timestamps, delivery date, PDF comment
 * @returns Zero or one mention string (multi-line) for the Sellsy document `note` field.
 */
export function buildEstimateNotes(
  _res: QuoteResult,
  state: QuoteState,
  ctx: EstimateNotesContext = {},
): string[] {
  const sections: string[] = [];

  const number = ctx.quoteNumber?.trim() || "";
  if (number) {
    // Prefer updatedAt, else validatedAt — always print the edition date/time on first publish too
    // (validated_at and updated_at are equal on the freeze transaction, so `>` would hide it).
    const editedAt = formatIsoDateTimeFr(ctx.updatedAt?.trim() || ctx.validatedAt?.trim() || "");
    if (editedAt) {
      sections.push(engineMessage("engineEstimateNoteQuoteEdition", { number, editedAt }));
    } else {
      sections.push(engineMessage("engineEstimateNoteQuoteNumber", { number }));
    }
  }

  const deliveryFr = formatIsoDayFr(ctx.deliveryDate);
  if (deliveryFr) sections.push(engineMessage("engineEstimateNoteDeliveryDate", { date: deliveryFr }));

  const comment = ctx.pdfComment?.trim() || state.header?.pdfComment?.trim() || "";
  if (comment) sections.push(engineMessage("engineEstimateNotePdfComment", { comment }));

  const delivery = quoteDeliveryLines(state.services ?? {});
  if (delivery.outbound.overallNote) {
    sections.push(engineMessage("engineEstimateNoteOutbound", { note: delivery.outbound.overallNote }));
  }
  if (delivery.endSite.overallNote) {
    sections.push(engineMessage("engineEstimateNoteEndSite", { note: delivery.endSite.overallNote }));
  }

  const fileReception = state.header?.backPlanning?.fileReception;
  const leadDays =
    fileReceptionLeadDays(fileReception, ctx.deliveryDate) ?? backPlanningDefaults().fileReceptionDays;
  sections.push(
    [
      engineMessage("engineEstimateProductionBlockTitle"),
      engineMessage("engineEstimateProductionLead", { days: leadDays }),
      engineMessage("engineEstimateProductionRule"),
    ].join("\n"),
  );

  return sections.length ? [sections.join("\n\n")] : [];
}
